import React from "react";

// Shared helper functions
const getKeyResponsibilities = (teamMember) => {
  if (teamMember?.key_responsibilities && teamMember.key_responsibilities.length > 0) {
    return teamMember.key_responsibilities;
  }
  if (teamMember?.activities_skills && teamMember.activities_skills.length > 0) {
    return teamMember.activities_skills.map(item => item.heading || item.desc);
  }
  return [
    "Strategic leadership and vision setting",
    "Team development and mentorship",
    "Stakeholder engagement and partnerships",
    "Innovation and process improvement"
  ];
};

const getAreasOfFocus = (teamMember) => {
  if (teamMember?.areas_of_focus && teamMember.areas_of_focus.length > 0) {
    return teamMember.areas_of_focus;
  }
  if (teamMember?.professional_skills && teamMember.professional_skills.length > 0) {
    return teamMember.professional_skills.map(skill => skill.heading);
  }
  const roleBasedTags = {
    "CEO": ["Strategic Planning", "Leadership", "Innovation", "Growth"],
    "Director": ["Operations", "Strategy", "Management", "Execution"],
    "Chairperson": ["Governance", "Strategy", "Advisory", "Leadership"],
    "Advisor": ["Consulting", "Expertise", "Guidance", "Strategy"]
  };
  return roleBasedTags[teamMember?.role] || ["Leadership", "Strategy", "Innovation"];
};

const getHighlights = (teamMember) => {
  if (teamMember?.highlights && teamMember.highlights.length > 0) {
    return teamMember.highlights;
  }
  if (teamMember?.key_contributions && teamMember.key_contributions.length > 0) {
    return teamMember.key_contributions;
  }
  return null;
};

const getShortBio = (teamMember) => {
  if (teamMember?.about) {
    return teamMember.about;
  }
  if (teamMember?.short_bio) {
    return teamMember.short_bio;
  }
  if (teamMember?.description) {
    const sentences = teamMember.description.split(/[.!?]+/).filter(s => s.trim().length > 0);
    return sentences.slice(0, 3).join(". ") + ".";
  }
  return "Experienced leader with a proven track record in strategic planning and team development. Passionate about innovation and driving organizational growth through collaborative leadership.";
};

const getOneLineIntro = (teamMember) => {
  if (teamMember?.one_line_intro) {
    return teamMember.one_line_intro;
  }
  if (teamMember?.tagline) {
    return teamMember.tagline;
  }
  const role = teamMember?.role || "Team Member";
  return `${role} dedicated to driving excellence and innovation.`;
};

// Variation 1: Modern Compact (Sidebar Style)
export const TeamDetailModernCompact = ({ teamMember }) => {
  const keyResponsibilities = getKeyResponsibilities(teamMember);
  const areasOfFocus = getAreasOfFocus(teamMember);
  const highlights = getHighlights(teamMember);
  const shortBio = getShortBio(teamMember);
  const oneLineIntro = getOneLineIntro(teamMember);

  return (
    <div className="team-detail-modern-compact">
      <div className="container" style={{ paddingTop: "3rem", paddingBottom: "3rem" }}>
        <div className="row">
          {/* Sidebar */}
          <div className="col-lg-4 mb-4 mb-lg-0">
            <div className="sidebar-card" style={{
              background: "#ffffff",
              borderRadius: "16px",
              padding: "2rem",
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              border: "1px solid #e2e8f0",
              position: "sticky",
              top: "100px"
            }}>
              <div className="text-center mb-4">
                <div
                  style={{
                    width: "160px",
                    height: "160px",
                    borderRadius: "50%",
                    backgroundImage: `url(${teamMember?.image || '/assets/img/dumy-person.jpg'})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    border: "4px solid #f7fafc",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    margin: "0 auto"
                  }}
                />
              </div>
              <h2 style={{ fontSize: "1.5rem", fontWeight: "700", color: "#1a202c", marginBottom: "0.5rem", textAlign: "center" }}>
                {teamMember?.name || "Team Member"}
              </h2>
              <p style={{ fontSize: "1rem", color: "#667eea", fontWeight: "600", marginBottom: "1rem", textAlign: "center" }}>
                {teamMember?.role || "Role"}
              </p>
              <p style={{ fontSize: "0.95rem", color: "#4a5568", lineHeight: "1.6", marginBottom: "1.5rem", textAlign: "center" }}>
                {oneLineIntro}
              </p>
              
              {/* Social Links */}
              <div style={{ display: "flex", justifyContent: "center", gap: "0.75rem", marginBottom: "1.5rem" }}>
                {teamMember?.social?.linkedin && (
                  <a href={teamMember.social.linkedin} target="_blank" rel="noopener noreferrer"
                    style={{ width: "40px", height: "40px", borderRadius: "50%", background: "#0077b5", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}>
                    <i className="fab fa-linkedin-in"></i>
                  </a>
                )}
                {teamMember?.contact?.email && (
                  <a href={`mailto:${teamMember.contact.email}`}
                    style={{ width: "40px", height: "40px", borderRadius: "50%", background: "#667eea", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}>
                    <i className="fas fa-envelope"></i>
                  </a>
                )}
              </div>
              
              {teamMember?.contact?.email && (
                <a href={`mailto:${teamMember.contact.email}`}
                  style={{ display: "block", width: "100%", padding: "0.75rem", background: "#667eea", color: "#fff", borderRadius: "8px", textAlign: "center", textDecoration: "none", fontWeight: "600", fontSize: "0.9rem" }}>
                  Contact
                </a>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="col-lg-8">
            {/* Bio */}
            <div style={{ background: "#ffffff", borderRadius: "12px", padding: "2rem", marginBottom: "1.5rem", boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
              <h3 style={{ fontSize: "1.25rem", fontWeight: "700", color: "#1a202c", marginBottom: "1rem" }}>About</h3>
              <p style={{ fontSize: "1rem", color: "#4a5568", lineHeight: "1.8", margin: "0" }}>{shortBio}</p>
            </div>

            {/* Responsibilities */}
            <div style={{ background: "#ffffff", borderRadius: "12px", padding: "2rem", marginBottom: "1.5rem", boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
              <h3 style={{ fontSize: "1.25rem", fontWeight: "700", color: "#1a202c", marginBottom: "1.25rem" }}>Key Responsibilities</h3>
              <ul style={{ listStyle: "none", padding: "0", margin: "0" }}>
                {keyResponsibilities.slice(0, 6).map((item, index) => (
                  <li key={index} style={{ padding: "0.625rem 0", paddingLeft: "1.5rem", position: "relative", fontSize: "0.95rem", color: "#4a5568", lineHeight: "1.7" }}>
                    <span style={{ position: "absolute", left: "0", top: "0.875rem", width: "6px", height: "6px", borderRadius: "50%", background: "#667eea" }}></span>
                    {typeof item === 'string' ? item : (item.heading || item.desc)}
                  </li>
                ))}
              </ul>
            </div>

            {/* Areas of Focus */}
            <div style={{ background: "#ffffff", borderRadius: "12px", padding: "2rem", marginBottom: "1.5rem", boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
              <h3 style={{ fontSize: "1.25rem", fontWeight: "700", color: "#1a202c", marginBottom: "1.25rem" }}>Areas of Focus</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.625rem" }}>
                {areasOfFocus.map((area, index) => (
                  <span key={index} style={{ padding: "0.5rem 1rem", background: "#edf2f7", color: "#2d3748", borderRadius: "20px", fontSize: "0.875rem", fontWeight: "500" }}>
                    {area}
                  </span>
                ))}
              </div>
            </div>

            {/* Highlights */}
            {highlights && highlights.length > 0 && (
              <div style={{ background: "#ffffff", borderRadius: "12px", padding: "2rem", marginBottom: "1.5rem", boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
                <h3 style={{ fontSize: "1.25rem", fontWeight: "700", color: "#1a202c", marginBottom: "1.25rem" }}>Key Contributions</h3>
                {highlights.slice(0, 3).map((highlight, index) => (
                  <div key={index} style={{ padding: "1rem", background: "#f7fafc", borderRadius: "8px", marginBottom: index < highlights.length - 1 ? "0.75rem" : "0", borderLeft: "3px solid #667eea" }}>
                    <p style={{ fontSize: "0.95rem", color: "#4a5568", lineHeight: "1.7", margin: "0" }}>
                      {typeof highlight === 'string' ? highlight : (highlight.title || highlight.description || highlight)}
                    </p>
                  </div>
                ))}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

// Variation 2: Modern Centered (All centered, wider cards)
export const TeamDetailModernCentered = ({ teamMember }) => {
  const keyResponsibilities = getKeyResponsibilities(teamMember);
  const areasOfFocus = getAreasOfFocus(teamMember);
  const highlights = getHighlights(teamMember);
  const shortBio = getShortBio(teamMember);
  const oneLineIntro = getOneLineIntro(teamMember);

  return (
    <div className="team-detail-modern-centered">
      {/* Hero Section */}
      <section style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", paddingTop: "4rem", paddingBottom: "3rem", color: "#ffffff" }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <div style={{ width: "180px", height: "180px", borderRadius: "50%", backgroundImage: `url(${teamMember?.image || '/assets/img/dumy-person.jpg'})`, backgroundSize: "cover", backgroundPosition: "center", border: "6px solid rgba(255,255,255,0.3)", boxShadow: "0 8px 32px rgba(0,0,0,0.2)", margin: "0 auto 1.5rem" }} />
              <h1 style={{ fontSize: "2.5rem", fontWeight: "700", color: "#ffffff", marginBottom: "0.75rem" }}>
                {teamMember?.name || "Team Member"}
              </h1>
              <p style={{ fontSize: "1.25rem", color: "rgba(255,255,255,0.9)", marginBottom: "1rem", fontWeight: "600" }}>
                {teamMember?.role || "Role"}
              </p>
              <p style={{ fontSize: "1.1rem", color: "rgba(255,255,255,0.85)", lineHeight: "1.7" }}>
                {oneLineIntro}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section style={{ background: "#f7fafc", paddingTop: "3rem", paddingBottom: "3rem" }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-9">
              {/* Bio */}
              <div style={{ background: "#ffffff", borderRadius: "16px", padding: "2.5rem", marginBottom: "2rem", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", textAlign: "center" }}>
                <h2 style={{ fontSize: "1.75rem", fontWeight: "700", color: "#1a202c", marginBottom: "1.25rem" }}>About</h2>
                <p style={{ fontSize: "1.05rem", color: "#4a5568", lineHeight: "1.9", margin: "0", maxWidth: "700px", marginLeft: "auto", marginRight: "auto" }}>
                  {shortBio}
                </p>
              </div>

              {/* Responsibilities */}
              <div style={{ background: "#ffffff", borderRadius: "16px", padding: "2.5rem", marginBottom: "2rem", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
                <h2 style={{ fontSize: "1.75rem", fontWeight: "700", color: "#1a202c", marginBottom: "1.5rem", textAlign: "center" }}>Key Responsibilities</h2>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "1.25rem" }}>
                  {keyResponsibilities.slice(0, 6).map((item, index) => (
                    <div key={index} style={{ padding: "1.25rem", background: "#f7fafc", borderRadius: "10px", borderLeft: "4px solid #667eea" }}>
                      <p style={{ fontSize: "0.95rem", color: "#4a5568", lineHeight: "1.7", margin: "0" }}>
                        {typeof item === 'string' ? item : (item.heading || item.desc)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Areas of Focus */}
              <div style={{ background: "#ffffff", borderRadius: "16px", padding: "2.5rem", marginBottom: "2rem", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", textAlign: "center" }}>
                <h2 style={{ fontSize: "1.75rem", fontWeight: "700", color: "#1a202c", marginBottom: "1.5rem" }}>Areas of Focus</h2>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", justifyContent: "center" }}>
                  {areasOfFocus.map((area, index) => (
                    <span key={index} style={{ padding: "0.625rem 1.5rem", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "#ffffff", borderRadius: "24px", fontSize: "0.95rem", fontWeight: "600" }}>
                      {area}
                    </span>
                  ))}
                </div>
              </div>

              {/* Highlights */}
              {highlights && highlights.length > 0 && (
                <div style={{ background: "#ffffff", borderRadius: "16px", padding: "2.5rem", marginBottom: "2rem", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
                  <h2 style={{ fontSize: "1.75rem", fontWeight: "700", color: "#1a202c", marginBottom: "1.5rem", textAlign: "center" }}>Key Contributions</h2>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.25rem" }}>
                    {highlights.slice(0, 3).map((highlight, index) => (
                      <div key={index} style={{ padding: "1.5rem", background: "linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)", borderRadius: "12px", textAlign: "center" }}>
                        <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: "#667eea", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1rem", fontSize: "1.25rem" }}>
                          <i className="fas fa-star"></i>
                        </div>
                        <p style={{ fontSize: "0.95rem", color: "#4a5568", lineHeight: "1.7", margin: "0" }}>
                          {typeof highlight === 'string' ? highlight : (highlight.title || highlight.description || highlight)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Social & CTA */}
              <div style={{ background: "#ffffff", borderRadius: "16px", padding: "2.5rem", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", textAlign: "center" }}>
                <div style={{ marginBottom: "2rem" }}>
                  <h3 style={{ fontSize: "1.5rem", fontWeight: "700", color: "#1a202c", marginBottom: "1.25rem" }}>Connect</h3>
                  <div style={{ display: "flex", justifyContent: "center", gap: "1rem" }}>
                    {teamMember?.social?.linkedin && (
                      <a href={teamMember.social.linkedin} target="_blank" rel="noopener noreferrer"
                        style={{ width: "48px", height: "48px", borderRadius: "50%", background: "#0077b5", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none", fontSize: "1.25rem" }}>
                        <i className="fab fa-linkedin-in"></i>
                      </a>
                    )}
                    {teamMember?.contact?.email && (
                      <a href={`mailto:${teamMember.contact.email}`}
                        style={{ width: "48px", height: "48px", borderRadius: "50%", background: "#667eea", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none", fontSize: "1.25rem" }}>
                        <i className="fas fa-envelope"></i>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Variation 3: Modern Split (Two Column Layout)
export const TeamDetailModernSplit = ({ teamMember }) => {
  const keyResponsibilities = getKeyResponsibilities(teamMember);
  const areasOfFocus = getAreasOfFocus(teamMember);
  const highlights = getHighlights(teamMember);
  const shortBio = getShortBio(teamMember);
  const oneLineIntro = getOneLineIntro(teamMember);

  return (
    <div className="team-detail-modern-split">
      {/* Header */}
      <section style={{ background: "#ffffff", paddingTop: "3rem", paddingBottom: "2rem" }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="row align-items-center">
                <div className="col-md-4 text-center mb-4 mb-md-0">
                  <div style={{ width: "200px", height: "200px", borderRadius: "50%", backgroundImage: `url(${teamMember?.image || '/assets/img/dumy-person.jpg'})`, backgroundSize: "cover", backgroundPosition: "center", border: "5px solid #f7fafc", boxShadow: "0 6px 24px rgba(0,0,0,0.12)", margin: "0 auto" }} />
                </div>
                <div className="col-md-8">
                  <h1 style={{ fontSize: "2.25rem", fontWeight: "700", color: "#1a202c", marginBottom: "0.5rem" }}>
                    {teamMember?.name || "Team Member"}
                  </h1>
                  <p style={{ fontSize: "1.25rem", color: "#667eea", fontWeight: "600", marginBottom: "1rem" }}>
                    {teamMember?.role || "Role"}
                  </p>
                  <p style={{ fontSize: "1.05rem", color: "#4a5568", lineHeight: "1.7", marginBottom: "1.5rem" }}>
                    {oneLineIntro}
                  </p>
                  <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                    {teamMember?.social?.linkedin && (
                      <a href={teamMember.social.linkedin} target="_blank" rel="noopener noreferrer"
                        style={{ padding: "0.5rem 1rem", background: "#0077b5", color: "#fff", borderRadius: "6px", textDecoration: "none", fontSize: "0.9rem", fontWeight: "600" }}>
                        <i className="fab fa-linkedin-in mr-2"></i>LinkedIn
                      </a>
                    )}
                    {teamMember?.contact?.email && (
                      <a href={`mailto:${teamMember.contact.email}`}
                        style={{ padding: "0.5rem 1rem", background: "#667eea", color: "#fff", borderRadius: "6px", textDecoration: "none", fontSize: "0.9rem", fontWeight: "600" }}>
                        <i className="fas fa-envelope mr-2"></i>Contact
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Two Column Content */}
      <section style={{ background: "#f7fafc", paddingTop: "2rem", paddingBottom: "3rem" }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="row">
                {/* Left Column */}
                <div className="col-md-6 mb-4">
                  {/* Bio */}
                  <div style={{ background: "#ffffff", borderRadius: "12px", padding: "2rem", marginBottom: "1.5rem", boxShadow: "0 1px 8px rgba(0,0,0,0.06)", height: "100%" }}>
                    <h3 style={{ fontSize: "1.5rem", fontWeight: "700", color: "#1a202c", marginBottom: "1rem" }}>About</h3>
                    <p style={{ fontSize: "1rem", color: "#4a5568", lineHeight: "1.8", margin: "0" }}>{shortBio}</p>
                  </div>

                  {/* Areas of Focus */}
                  <div style={{ background: "#ffffff", borderRadius: "12px", padding: "2rem", boxShadow: "0 1px 8px rgba(0,0,0,0.06)", height: "100%" }}>
                    <h3 style={{ fontSize: "1.5rem", fontWeight: "700", color: "#1a202c", marginBottom: "1.25rem" }}>Areas of Focus</h3>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.625rem" }}>
                      {areasOfFocus.map((area, index) => (
                        <span key={index} style={{ padding: "0.5rem 1rem", background: "#edf2f7", color: "#2d3748", borderRadius: "20px", fontSize: "0.875rem", fontWeight: "500" }}>
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="col-md-6 mb-4">
                  {/* Responsibilities */}
                  <div style={{ background: "#ffffff", borderRadius: "12px", padding: "2rem", marginBottom: "1.5rem", boxShadow: "0 1px 8px rgba(0,0,0,0.06)", height: "100%" }}>
                    <h3 style={{ fontSize: "1.5rem", fontWeight: "700", color: "#1a202c", marginBottom: "1.25rem" }}>Key Responsibilities</h3>
                    <ul style={{ listStyle: "none", padding: "0", margin: "0" }}>
                      {keyResponsibilities.slice(0, 6).map((item, index) => (
                        <li key={index} style={{ padding: "0.75rem 0", paddingLeft: "1.75rem", position: "relative", fontSize: "0.95rem", color: "#4a5568", lineHeight: "1.7" }}>
                          <span style={{ position: "absolute", left: "0", top: "0.875rem", width: "8px", height: "8px", borderRadius: "50%", background: "#667eea" }}></span>
                          {typeof item === 'string' ? item : (item.heading || item.desc)}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Highlights */}
                  {highlights && highlights.length > 0 && (
                    <div style={{ background: "#ffffff", borderRadius: "12px", padding: "2rem", boxShadow: "0 1px 8px rgba(0,0,0,0.06)", height: "100%" }}>
                      <h3 style={{ fontSize: "1.5rem", fontWeight: "700", color: "#1a202c", marginBottom: "1.25rem" }}>Key Contributions</h3>
                      {highlights.slice(0, 3).map((highlight, index) => (
                        <div key={index} style={{ padding: "1rem", background: "#f7fafc", borderRadius: "8px", marginBottom: index < highlights.length - 1 ? "0.75rem" : "0", borderLeft: "3px solid #667eea" }}>
                          <p style={{ fontSize: "0.95rem", color: "#4a5568", lineHeight: "1.7", margin: "0" }}>
                            {typeof highlight === 'string' ? highlight : (highlight.title || highlight.description || highlight)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Variation 4: Modern Minimal (Ultra Clean)
export const TeamDetailModernMinimal = ({ teamMember }) => {
  const keyResponsibilities = getKeyResponsibilities(teamMember);
  const areasOfFocus = getAreasOfFocus(teamMember);
  const highlights = getHighlights(teamMember);
  const shortBio = getShortBio(teamMember);
  const oneLineIntro = getOneLineIntro(teamMember);

  return (
    <div className="team-detail-modern-minimal">
      <div className="container" style={{ paddingTop: "4rem", paddingBottom: "4rem", maxWidth: "800px" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "4rem" }}>
          <div style={{ width: "150px", height: "150px", borderRadius: "50%", backgroundImage: `url(${teamMember?.image || '/assets/img/dumy-person.jpg'})`, backgroundSize: "cover", backgroundPosition: "center", margin: "0 auto 1.5rem", border: "3px solid #e2e8f0" }} />
          <h1 style={{ fontSize: "2rem", fontWeight: "700", color: "#1a202c", marginBottom: "0.5rem" }}>
            {teamMember?.name || "Team Member"}
          </h1>
          <p style={{ fontSize: "1.125rem", color: "#667eea", fontWeight: "600", marginBottom: "1rem" }}>
            {teamMember?.role || "Role"}
          </p>
          <p style={{ fontSize: "1rem", color: "#718096", lineHeight: "1.7", maxWidth: "600px", margin: "0 auto 2rem" }}>
            {oneLineIntro}
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: "1rem" }}>
            {teamMember?.social?.linkedin && (
              <a href={teamMember.social.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: "#4a5568", fontSize: "1.25rem", textDecoration: "none" }}>
                <i className="fab fa-linkedin-in"></i>
              </a>
            )}
            {teamMember?.contact?.email && (
              <a href={`mailto:${teamMember.contact.email}`} style={{ color: "#4a5568", fontSize: "1.25rem", textDecoration: "none" }}>
                <i className="fas fa-envelope"></i>
              </a>
            )}
          </div>
        </div>

        {/* Bio */}
        <div style={{ marginBottom: "3rem", paddingBottom: "3rem", borderBottom: "1px solid #e2e8f0" }}>
          <h2 style={{ fontSize: "1.25rem", fontWeight: "700", color: "#1a202c", marginBottom: "1rem" }}>About</h2>
          <p style={{ fontSize: "1rem", color: "#4a5568", lineHeight: "1.9", margin: "0" }}>{shortBio}</p>
        </div>

        {/* Responsibilities */}
        <div style={{ marginBottom: "3rem", paddingBottom: "3rem", borderBottom: "1px solid #e2e8f0" }}>
          <h2 style={{ fontSize: "1.25rem", fontWeight: "700", color: "#1a202c", marginBottom: "1.5rem" }}>Key Responsibilities</h2>
          <ul style={{ listStyle: "none", padding: "0", margin: "0" }}>
            {keyResponsibilities.slice(0, 6).map((item, index) => (
              <li key={index} style={{ padding: "0.75rem 0", fontSize: "0.95rem", color: "#4a5568", lineHeight: "1.8" }}>
                • {typeof item === 'string' ? item : (item.heading || item.desc)}
              </li>
            ))}
          </ul>
        </div>

        {/* Areas of Focus */}
        <div style={{ marginBottom: "3rem", paddingBottom: "3rem", borderBottom: "1px solid #e2e8f0" }}>
          <h2 style={{ fontSize: "1.25rem", fontWeight: "700", color: "#1a202c", marginBottom: "1.5rem" }}>Areas of Focus</h2>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {areasOfFocus.map((area, index) => (
              <span key={index} style={{ padding: "0.375rem 0.875rem", background: "#f7fafc", color: "#4a5568", borderRadius: "4px", fontSize: "0.875rem" }}>
                {area}
              </span>
            ))}
          </div>
        </div>

        {/* Highlights */}
        {highlights && highlights.length > 0 && (
          <div style={{ marginBottom: "3rem", paddingBottom: "3rem", borderBottom: "1px solid #e2e8f0" }}>
            <h2 style={{ fontSize: "1.25rem", fontWeight: "700", color: "#1a202c", marginBottom: "1.5rem" }}>Key Contributions</h2>
            {highlights.slice(0, 3).map((highlight, index) => (
              <p key={index} style={{ fontSize: "0.95rem", color: "#4a5568", lineHeight: "1.8", marginBottom: "1rem" }}>
                {typeof highlight === 'string' ? highlight : (highlight.title || highlight.description || highlight)}
              </p>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

// Variation 5: Modern Card Grid (Card-based sections)
export const TeamDetailModernCardGrid = ({ teamMember }) => {
  const keyResponsibilities = getKeyResponsibilities(teamMember);
  const areasOfFocus = getAreasOfFocus(teamMember);
  const highlights = getHighlights(teamMember);
  const shortBio = getShortBio(teamMember);
  const oneLineIntro = getOneLineIntro(teamMember);

  return (
    <div className="team-detail-modern-card-grid">
      {/* Header Card */}
      <section style={{ background: "#f7fafc", paddingTop: "3rem", paddingBottom: "2rem" }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div style={{ background: "#ffffff", borderRadius: "20px", padding: "3rem", boxShadow: "0 4px 20px rgba(0,0,0,0.08)", textAlign: "center" }}>
                <div style={{ width: "160px", height: "160px", borderRadius: "50%", backgroundImage: `url(${teamMember?.image || '/assets/img/dumy-person.jpg'})`, backgroundSize: "cover", backgroundPosition: "center", border: "5px solid #f7fafc", boxShadow: "0 6px 24px rgba(0,0,0,0.12)", margin: "0 auto 1.5rem" }} />
                <h1 style={{ fontSize: "2.25rem", fontWeight: "700", color: "#1a202c", marginBottom: "0.5rem" }}>
                  {teamMember?.name || "Team Member"}
                </h1>
                <p style={{ fontSize: "1.25rem", color: "#667eea", fontWeight: "600", marginBottom: "1rem" }}>
                  {teamMember?.role || "Role"}
                </p>
                <p style={{ fontSize: "1.05rem", color: "#4a5568", lineHeight: "1.7", marginBottom: "1.5rem" }}>
                  {oneLineIntro}
                </p>
                <div style={{ display: "flex", justifyContent: "center", gap: "0.75rem" }}>
                  {teamMember?.social?.linkedin && (
                    <a href={teamMember.social.linkedin} target="_blank" rel="noopener noreferrer"
                      style={{ width: "44px", height: "44px", borderRadius: "50%", background: "#0077b5", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}>
                      <i className="fab fa-linkedin-in"></i>
                    </a>
                  )}
                  {teamMember?.contact?.email && (
                    <a href={`mailto:${teamMember.contact.email}`}
                      style={{ width: "44px", height: "44px", borderRadius: "50%", background: "#667eea", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}>
                      <i className="fas fa-envelope"></i>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Card Grid */}
      <section style={{ background: "#f7fafc", paddingTop: "2rem", paddingBottom: "3rem" }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="row">
                {/* Bio Card */}
                <div className="col-md-6 mb-4">
                  <div style={{ background: "#ffffff", borderRadius: "16px", padding: "2rem", height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
                    <div style={{ width: "48px", height: "48px", borderRadius: "12px", background: "#667eea", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "1.25rem", fontSize: "1.25rem" }}>
                      <i className="fas fa-user"></i>
                    </div>
                    <h3 style={{ fontSize: "1.5rem", fontWeight: "700", color: "#1a202c", marginBottom: "1rem" }}>About</h3>
                    <p style={{ fontSize: "0.95rem", color: "#4a5568", lineHeight: "1.8", margin: "0" }}>{shortBio}</p>
                  </div>
                </div>

                {/* Responsibilities Card */}
                <div className="col-md-6 mb-4">
                  <div style={{ background: "#ffffff", borderRadius: "16px", padding: "2rem", height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
                    <div style={{ width: "48px", height: "48px", borderRadius: "12px", background: "#667eea", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "1.25rem", fontSize: "1.25rem" }}>
                      <i className="fas fa-tasks"></i>
                    </div>
                    <h3 style={{ fontSize: "1.5rem", fontWeight: "700", color: "#1a202c", marginBottom: "1.25rem" }}>Key Responsibilities</h3>
                    <ul style={{ listStyle: "none", padding: "0", margin: "0" }}>
                      {keyResponsibilities.slice(0, 4).map((item, index) => (
                        <li key={index} style={{ padding: "0.5rem 0", paddingLeft: "1.5rem", position: "relative", fontSize: "0.9rem", color: "#4a5568", lineHeight: "1.7" }}>
                          <span style={{ position: "absolute", left: "0", top: "0.7rem", width: "6px", height: "6px", borderRadius: "50%", background: "#667eea" }}></span>
                          {typeof item === 'string' ? item : (item.heading || item.desc)}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Areas of Focus Card */}
                <div className="col-md-6 mb-4">
                  <div style={{ background: "#ffffff", borderRadius: "16px", padding: "2rem", height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
                    <div style={{ width: "48px", height: "48px", borderRadius: "12px", background: "#667eea", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "1.25rem", fontSize: "1.25rem" }}>
                      <i className="fas fa-bullseye"></i>
                    </div>
                    <h3 style={{ fontSize: "1.5rem", fontWeight: "700", color: "#1a202c", marginBottom: "1.25rem" }}>Areas of Focus</h3>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.625rem" }}>
                      {areasOfFocus.map((area, index) => (
                        <span key={index} style={{ padding: "0.5rem 1rem", background: "#edf2f7", color: "#2d3748", borderRadius: "20px", fontSize: "0.875rem", fontWeight: "500" }}>
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Highlights Card */}
                {highlights && highlights.length > 0 ? (
                  <div className="col-md-6 mb-4">
                    <div style={{ background: "#ffffff", borderRadius: "16px", padding: "2rem", height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
                      <div style={{ width: "48px", height: "48px", borderRadius: "12px", background: "#667eea", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "1.25rem", fontSize: "1.25rem" }}>
                        <i className="fas fa-star"></i>
                      </div>
                      <h3 style={{ fontSize: "1.5rem", fontWeight: "700", color: "#1a202c", marginBottom: "1.25rem" }}>Key Contributions</h3>
                      {highlights.slice(0, 3).map((highlight, index) => (
                        <div key={index} style={{ padding: "0.875rem", background: "#f7fafc", borderRadius: "8px", marginBottom: index < highlights.length - 1 ? "0.75rem" : "0" }}>
                          <p style={{ fontSize: "0.9rem", color: "#4a5568", lineHeight: "1.7", margin: "0" }}>
                            {typeof highlight === 'string' ? highlight : (highlight.title || highlight.description || highlight)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
